"use client"
import { useState } from "react"
import { motion } from "framer-motion"

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    service: "customer-service",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState("")

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError("")

    try {
      // This would be replaced with an actual API call
      await new Promise((resolve) => setTimeout(resolve, 1500))
      setIsSubmitted(true)
      // Reset form
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        message: "",
        service: "customer-service",
      })
    } catch (err) {
      setError("There was an error submitting your message. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const offices = [
    {
      city: "Sacramento",
      address: "3400 Cottage Way, Sacramento, CA 95825",
      phone: "+1 (765) 237-7300",
      email: "info@alfastech.com",
    },
    {
      city: "Toronto",
      address: "627 King St W, Toronto, ON M5V 1M5, Canada",
      phone: "+1 (437) 606-9948",
      email: "info@alfastech.com",
    },
    {
      city: "Lahore",
      address: "1AC, College Road, Lahore, Pakistan",
      phone: "+92 332 0440836",
      email: "info@alfastech.com",
    },
  ]

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-background">
            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(#5F72FF22 1px, transparent 1px), linear-gradient(90deg, #5F72FF22 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
              ></div>
            </div>

            {/* Animated gradient blobs */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0.3 }}
              animate={{
                scale: [0.8, 1.2, 0.9, 1.1, 0.8],
                opacity: [0.3, 0.4, 0.2, 0.5, 0.3],
                x: ["-10%", "5%", "0%", "-5%", "-10%"],
                y: ["5%", "-5%", "10%", "0%", "5%"],
              }}
              transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, repeatType: "loop" }}
              className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] rounded-full bg-accent opacity-20 blur-[100px]"
            />
          </div>
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-futuristic"
            >
              Get in Touch
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white mb-8"
            >
              Have questions about our AI solutions? Ready to transform your business? We're here to help.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="backdrop-blur-xl bg-white/10 p-8 md:p-10 rounded-2xl border-2 border-white/20 shadow-xl">
                <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Send Us a Message</h2>
                
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-accent/20 border border-accent/50 rounded-xl p-6 text-center"
                  >
                    <svg className="w-16 h-16 text-accent mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                    <p className="text-white">Your message has been sent successfully. We'll get back to you shortly.</p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="mt-6 px-6 py-3 rounded-full font-bold text-white bg-accent hover:bg-accent/80 transition-all duration-300"
                    >
                      Send Another Message
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-white mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-white mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="company" className="block text-white mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-white mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="service" className="block text-white mb-2">
                        Service of Interest *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="customer-service" className="bg-background">AI-Powered Customer Service</option>
                        <option value="order-management" className="bg-background">AI-Driven Order Management</option>
                        <option value="analytics" className="bg-background">Advanced Analytics & Insights</option>
                        <option value="custom" className="bg-background">Custom Solution</option>
                      </select>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="message" className="block text-white mb-2">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50"
                      ></textarea>
                    </div>

                    {error && (
                      <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-white">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:shadow-accent/50 hover:scale-105 transition-all duration-300 text-white border-2 border-white/20 ${
                        isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        "Send Message"
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="space-y-8">
                <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 shadow-xl">
                  <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Contact Information</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="mr-4 mt-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/20 text-accent">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-1">Email</h3>
                        <p className="text-white">
                          <a href="mailto:info@alfastech.com" className="hover:text-accent transition-colors">info@alfastech.com</a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="mr-4 mt-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/20 text-accent">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-1">Phone</h3>
                        <p className="text-white">
                          <a href="tel:+17652377300" className="hover:text-accent transition-colors">+1 (765) 237-7300</a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="mr-4 mt-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/20 text-accent">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-1">Business Hours</h3>
                        <p className="text-white">Monday - Friday: 9:00 AM - 6:00 PM PST</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 shadow-xl">
                  <h2 className="text-3xl font-bold text-white mb-6 font-futuristic">Our Offices</h2>
                  
                  <div className="space-y-8">
                    {offices.map((office, index) => (
                      <motion.div
                        key={office.city}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="flex items-start"
                      >
                        <div className="mr-4 mt-1">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/20 text-accent">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-1">{office.city}</h3>
                          <p className="text-white mb-2">{office.address}</p>
                          <p className="text-white">
                            <a href={`tel:${office.phone}`} className="hover:text-accent transition-colors">{office.phone}</a>
                          </p>
                          <p className="text-white">
                            <a href={`mailto:${office.email}`} className="hover:text-accent transition-colors">{office.email}</a>
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4 font-futuristic"
            >
              Our Global Presence
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              With offices across the globe, we're ready to serve your business wherever you are
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="backdrop-blur-xl bg-white/10 p-6 rounded-2xl border-2 border-white/20 shadow-xl overflow-hidden"
          >
            <div className="relative w-full h-96">
              {/* World Map SVG */}
              <img src="/world.svg" alt="World Map" className="absolute inset-0 w-full h-full opacity-50" />

              {/* Office locations */}
              {[
                  { name: "USA", x: "12%", y: "50%", isMain: true },
                  { name: "Canada", x: "17%", y: "40%", isMain: false },
                  { name: "Saudi Arabia", x: "57%", y: "57%", isMain: true },
                  { name: "Pakistan", x: "65%", y: "53%", isMain: true },
                  { name: "Australia", x: "80%", y: "82%", isMain: false },
                  
                ].map((location, index) => (
                <motion.div
                  key={location.name}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.2 }}
                  className={`absolute flex flex-col items-center z-10`}
                  style={{ left: location.x, top: location.y }}
                >
                  <div
                    className={`w-4 h-4 rounded-full ${
                      location.isMain ? "bg-gradient-to-r from-accent to-accent2" : "bg-highlight"
                    } mb-1 animate-pulse-slow shadow-lg shadow-accent/30`}
                  ></div>
                  <span className="text-xs font-medium text-white bg-background/80 px-2 py-1 rounded-full backdrop-blur-sm border border-white/20">
                    {location.name}
                  </span>
                </motion.div>
              ))}

              {/* Connection lines */}
              <svg className="absolute inset-0 w-full h-full z-0 opacity-50" viewBox="0 0 100 100" preserveAspectRatio="none">
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.5 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, delay: 1 }}
                  d="M20,40 C40,20 50,60 70,40"
                  stroke="url(#lineGradient)"
                  strokeWidth="0.5"
                  fill="none"
                />
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.5 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, delay: 1.3 }}
                  d="M45,30 C50,50 60,40 70,45"
                  stroke="url(#lineGradient)"
                  strokeWidth="0.5"
                  fill="none"
                />
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#5F72FF" />
                    <stop offset="100%" stopColor="#9F53FF" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-accent/30 to-accent2/30 relative overflow-hidden">
        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-accent opacity-20 blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-accent2 opacity-20 blur-3xl animate-pulse-slower"></div>

        <div className="container-custom relative z-10">
          <div className="backdrop-blur-lg bg-background/80 border border-white/20 rounded-3xl p-12 md:p-16 max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold mb-10 text-white text-center font-futuristic"
            >
              Frequently Asked Questions
            </motion.h2>

            <div className="space-y-6">
              {[
                {
                  question: "How quickly can you implement your AI solutions?",
                  answer: "Implementation timelines vary based on the complexity of your needs, but typically range from 2-8 weeks. Our team works efficiently to ensure minimal disruption to your operations while providing comprehensive training and support throughout the process."
                },
                {
                  question: "Do your AI solutions integrate with existing systems?",
                  answer: "Yes, our solutions are designed to seamlessly integrate with most major CRM, ERP, and customer service platforms. We have pre-built connectors for systems like Salesforce, Zendesk, SAP, and many others. For custom or legacy systems, our team can develop tailored integration solutions."
                },
                {
                  question: "How do you ensure data security and privacy?",
                  answer: "We implement enterprise-grade security measures including end-to-end encryption, secure authentication protocols, and regular security audits. Our solutions are compliant with GDPR, CCPA, and other relevant data protection regulations. We also provide data residency options to meet specific regional requirements."
                },
                {
                  question: "What kind of support do you provide after implementation?",
                  answer: "We offer comprehensive post-implementation support including 24/7 technical assistance, regular maintenance updates, performance optimization, and continuous model training. Our support packages can be tailored to your specific needs, from basic technical support to full managed services."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 overflow-hidden"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3">{faq.question}</h3>
                    <p className="text-white">{faq.answer}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
